import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import OpenButton from "@/components/toto/OpenButton";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarClock, Clock3, Sparkles, PlusCircle, Users, Send,
  ChevronRight, HandHeart,
} from "lucide-react";

/**
 * My Schedule — the personal doorway.
 *
 * Reads plainly. No admin language, no workflow terminology. Everything an
 * employee needs to know branches from this page. Managers can still visit
 * it as a preview of what their team sees; the sidebar entry is front-line
 * only.
 */

const MOMENT_TONE = {
  action_required:    { dot: "bg-amber-500",   glass: "qg-moment-amber" },
  response_received:  { dot: "bg-emerald-500", glass: "qg-moment-emerald" },
  approval_received:  { dot: "bg-emerald-500", glass: "qg-moment-emerald" },
  info:               { dot: "bg-sky-500",     glass: "qg-moment-sky" },
};

function ActiveMoment({ moment }) {
  if (!moment) return null;
  const t = MOMENT_TONE[moment.tone] || MOMENT_TONE.info;
  return (
    <div className={`rounded-3xl p-5 quiet-glass-moment ${t.glass}`} data-testid="active-moment">
      <div className="flex items-center gap-2 text-[11px] label-eyebrow relative">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${t.dot}`} />
        <span data-testid="active-moment-eyebrow">{moment.eyebrow}</span>
      </div>
      <div className="mt-2 text-lg relative" data-testid="active-moment-message">{moment.message}</div>
      {moment.action_link && moment.action_label && (
        <OpenButton to={moment.action_link} data-testid="active-moment-action" aria-label={moment.action_label}/>
      )}
    </div>
  );
}

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function relDay(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
  if (diff > 1 && diff < 7) return weekday;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function greeting(name) {
  const h = new Date().getHours();
  const bit = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const first = (name || "").split(" ")[0] || "";
  return `${bit}${first ? `, ${first}` : ""}.`;
}

function Section({ title, action, children, testid }) {
  return (
    <section className="space-y-3" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif-display text-stone-900 text-xl">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function NextShiftCard({ shift }) {
  if (!shift) {
    return (
      <div className="bg-white subtle-border rounded-3xl p-6 card-shadow" data-testid="my-next-shift-empty">
        <div className="text-sm text-stone-500">No upcoming shifts on your published schedule right now.</div>
      </div>
    );
  }
  return (
    <div className="bg-white subtle-border rounded-3xl p-6 sm:p-7 card-shadow" data-testid="my-next-shift">
      <div className="text-[11px] label-eyebrow text-stone-400">Next shift</div>
      <div className="mt-2 font-serif-display text-stone-900 text-3xl sm:text-4xl">
        {relDay(shift.date)}
      </div>
      <div className="mt-1 text-stone-700 text-lg">
        {fmtTime(shift.start)} – {fmtTime(shift.end)}
      </div>
      {shift.notes && <div className="text-xs text-stone-500 mt-2">{shift.notes}</div>}
    </div>
  );
}

function UpcomingList({ upcoming, nextId }) {
  if (!upcoming?.length) return null;
  // Group by date
  const byDate = {};
  for (const s of upcoming) {
    (byDate[s.date] ||= []).push(s);
  }
  const dates = Object.keys(byDate).sort();
  return (
    <div className="bg-white subtle-border rounded-2xl overflow-hidden" data-testid="my-upcoming-list">
      <ul className="divide-y divide-stone-100">
        {dates.slice(0, 10).map((d, idx) => (
          <li key={d} className="px-5 py-3 flex items-center gap-4" data-testid={`my-upcoming-${d}`}>
            <div className="w-28 shrink-0">
              <div className="text-sm text-stone-900">{relDay(d)}</div>
              <div className="text-[11px] text-stone-500">{new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              {byDate[d].map((s, i) => (
                <div key={i} className="text-sm text-stone-700">
                  {fmtTime(s.start)} – {fmtTime(s.end)}
                  {s.notes && <span className="text-[11px] text-stone-500 ml-2">{s.notes}</span>}
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActionRow({ testid, icon: Icon, title, subtitle, to, badge }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-4 px-5 py-4 bg-white subtle-border rounded-2xl hover-lift transition-colors"
    >
      <div className="w-10 h-10 rounded-2xl bg-stone-50 subtle-border flex items-center justify-center text-stone-700">
        <Icon size={16} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-stone-900 flex items-center gap-2">
          <span>{title}</span>
          {badge != null && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{badge}</span>}
        </div>
        <div className="text-xs text-stone-500 mt-0.5">{subtitle}</div>
      </div>
      <ChevronRight size={20} strokeWidth={2} className="text-stone-300 group-hover:text-stone-500 transition-colors" />
    </Link>
  );
}

function TimeOffMiniList({ requests }) {
  if (!requests?.length) return null;
  const active = requests.filter((r) => r.status !== "complete" && r.status !== "declined").slice(0, 3);
  if (active.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid="my-time-off-mini">
      {active.map((r) => {
        const o = r.approved_outcome || r.request;
        return (
          <Link key={r.id} to="/scheduling/time-off" data-testid={`my-time-off-item-${r.id}`}
            className="flex items-center gap-2 text-[11px] text-stone-600 hover:text-stone-900">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
            <span>{o.start_date === o.end_date ? o.start_date : `${o.start_date} → ${o.end_date}`}</span>
            <span className="text-stone-400">·</span>
            <span className="text-stone-500">{r.status.replace(/_/g, " ")}</span>
          </Link>
        );
      })}
    </div>
  );
}

function RecentChanges({ changes }) {
  if (!changes?.length) {
    return (
      <div className="bg-white subtle-border rounded-2xl p-5 text-sm text-stone-500" data-testid="my-recent-empty">
        Nothing new to catch you up on.
      </div>
    );
  }
  return (
    <ul className="bg-white subtle-border rounded-2xl divide-y divide-stone-100" data-testid="my-recent-list">
      {changes.slice(0, 6).map((c, i) => (
        <li key={i} className="px-5 py-3 flex items-start gap-3 text-sm" data-testid={`my-recent-${i}`}>
          <ChevronRight size={12} strokeWidth={1.5} className="text-stone-300 mt-1 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-stone-900">{c.summary}</div>
            {c.detail && <div className="text-[11px] text-stone-500 mt-0.5">{c.detail}</div>}
            {c.at && <div className="text-[11px] text-stone-400 mt-0.5">{new Date(c.at).toLocaleString()}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function MySchedule() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [moment, setMoment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/scheduling/me"),
      api.get("/scheduling/me/moment"),
    ]).then(([me, mom]) => {
      setData(me.data);
      setMoment(mom.data?.moment || null);
    }).catch(() => { setData(null); setMoment(null); })
      .finally(() => setLoading(false));
  }, []);

  const openTimeOff = useMemo(() => {
    if (!data?.my_time_off) return 0;
    return data.my_time_off.filter((r) => ["requested", "approved", "schedule_updated", "zenoti_pending"].includes(r.status)).length;
  }, [data]);

  return (
    <div className="min-h-screen bg-stone-50">
      <Topbar title="My Schedule" />
      <div className="max-w-4xl mx-auto px-4 sm:px-10 py-8 sm:py-12">
        <div>
          <h1 className="font-serif-display text-stone-900 text-4xl sm:text-5xl " style={{ fontWeight: 400 }}>
            {greeting(user?.name)}
          </h1>
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-stone-500" data-testid="my-schedule-loading">Loading…</div>
        ) : !data ? (
          <div className="mt-10 text-sm text-stone-500">We couldn't load your schedule right now.</div>
        ) : (
          <div className="mt-10 space-y-10">
            {/* Active Moment — the single conversational message. Silent when nothing's actionable. */}
            {moment && user?.role !== "front_desk" && <ActiveMoment moment={moment} />}

            {/* Next shift */}
            <NextShiftCard shift={data.next_shift} />

            {/* Upcoming */}
            {data.upcoming?.length > 0 && (
              <Section title="Upcoming" testid="my-upcoming-section">
                <UpcomingList upcoming={data.upcoming} />
              </Section>
            )}

            {/* Actions */}
            {user?.role === "front_desk" ? <Section title="Scheduling preferences" testid="my-preferences-section">
              <ActionRow testid="my-scheduling-preferences" icon={CalendarClock} title="My scheduling preferences" subtitle="Share your availability preferences with your manager" to="/scheduling/preferences" />
            </Section> : <Section title="Your requests" testid="my-actions-section">
              <div className="space-y-2">
                <ActionRow
                  testid="my-request-time-off"
                  icon={PlusCircle}
                  title="Request time off"
                  subtitle="Full day or partial · your manager sees this within moments"
                  to="/scheduling/time-off"
                  badge={openTimeOff > 0 ? `${openTimeOff} open` : null}
                />
                {openTimeOff > 0 && <TimeOffMiniList requests={data.my_time_off} />}
                <ActionRow
                  testid="my-need-coverage"
                  icon={HandHeart}
                  title="Need coverage?"
                  subtitle="Post a shift you can't work — teammates can volunteer"
                  to="/help-a-teammate?mine=1"
                />
                <ActionRow
                  testid="my-available-coverage"
                  icon={Sparkles}
                  title="Help a teammate"
                  subtitle="Shifts your teammates are looking for coverage on"
                  to="/help-a-teammate"
                />
                <ActionRow
                  testid="my-pending-swaps"
                  icon={Send}
                  title="Shift swaps"
                  subtitle="Post one of your shifts to swap — your GM approves the winner"
                  to="/help-a-teammate/swaps"
                />
              </div>
            </Section>}

            {/* Recent */}
            <Section title="Recent schedule changes" testid="my-recent-section">
              <RecentChanges changes={data.recent_changes} />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
